import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { adminAPI } from '../../services/api';
import {
  Mail, Server, Key, Settings, Send, Check, X, AlertCircle,
  Eye, EyeOff, RefreshCw, Save, ChevronDown, ChevronRight,
  FileText, Edit3, Trash2, Plus, Copy
} from 'lucide-react';

const AdminEmailSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  
  // Email settings
  const [settings, setSettings] = useState({
    provider: 'smtp',
    is_enabled: false,
    smtp: {
      host: '',
      port: 587,
      username: '',
      password: '',
      use_tls: true,
      use_ssl: false,
      from_email: '',
      from_name: 'ExDex'
    },
    resend: {
      api_key: '',
      from_email: '',
      from_name: 'ExDex'
    },
    sendgrid: {
      api_key: '',
      from_email: '',
      from_name: 'ExDex'
    }
  });
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');

  useEffect(() => {
    loadSettings();
    loadTemplates();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await adminAPI.getEmailSettings();
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await adminAPI.getEmailTemplates();
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await adminAPI.updateEmailSettings(settings);
      setTestResult({ success: true, message: 'Настройки сохранены!' });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || 'Ошибка сохранения' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setTestResult({ success: false, message: 'Введите email для тестирования' });
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    try {
      const response = await adminAPI.sendTestEmail({ to_email: testEmail });
      setTestResult({ success: true, message: response.data?.message || 'Тестовое письмо отправлено!' });
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || 'Ошибка отправки' });
    } finally {
      setTesting(false);
    }
  };

  const handleUpdateSmtp = (field, value) => {
    setSettings(prev => ({
      ...prev,
      smtp: { ...prev.smtp, [field]: value }
    }));
  };

  const handleUpdateApi = (provider, field, value) => {
    setSettings(prev => ({
      ...prev,
      [provider]: { ...prev[provider], [field]: value }
    }));
  };

  const handlePreviewTemplate = async (template) => {
    try {
      const response = await adminAPI.previewEmailTemplate(template.id, {});
      setPreviewHtml(response.data?.body_html || '');
      setSelectedTemplate(template);
    } catch (error) {
      console.error('Error previewing template:', error);
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      await adminAPI.updateEmailTemplate(editingTemplate.id, {
        subject: editingTemplate.subject,
        body_html: editingTemplate.body_html,
        body_text: editingTemplate.body_text,
        is_active: editingTemplate.is_active
      });
      loadTemplates();
      setEditingTemplate(null);
      setTestResult({ success: true, message: 'Шаблон сохранён!' });
      setTimeout(() => setTestResult(null), 3000);
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || 'Ошибка сохранения' });
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'smtp': return <Server size={20} />;
      case 'resend': return <Mail size={20} />;
      case 'sendgrid': return <Send size={20} />;
      default: return <Mail size={20} />;
    }
  };

  const getTemplateTypeLabel = (type) => {
    const labels = {
      'welcome': '📧 Приветственное письмо',
      '2fa': '🔐 Код 2FA',
      'password_reset': '🔑 Восстановление пароля',
      'transaction': '💰 Уведомление о транзакции',
      'custom': '✏️ Произвольный шаблон'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <RefreshCw size={32} color={COLORS.primary} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: COLORS.text, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Mail size={28} color={COLORS.primary} />
          Настройки Email
        </h1>
        <p style={{ color: COLORS.textSecondary }}>
          Настройте отправку email уведомлений клиентам платформы
        </p>
      </div>

      {/* Status Alert */}
      {testResult && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          backgroundColor: testResult.success ? `${COLORS.success}20` : `${COLORS.danger}20`,
          border: `1px solid ${testResult.success ? COLORS.success : COLORS.danger}`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          {testResult.success ? <Check size={18} color={COLORS.success} /> : <AlertCircle size={18} color={COLORS.danger} />}
          <span style={{ color: testResult.success ? COLORS.success : COLORS.danger }}>{testResult.message}</span>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: `1px solid ${COLORS.border}`, paddingBottom: '8px' }}>
        {[
          { id: 'settings', label: 'Настройки', icon: <Settings size={18} /> },
          { id: 'templates', label: 'Шаблоны писем', icon: <FileText size={18} /> }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: activeTab === tab.id ? COLORS.primary : 'transparent',
              color: activeTab === tab.id ? '#000' : COLORS.textSecondary,
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'settings' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Left Column - Provider Settings */}
          <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '24px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: COLORS.text, marginBottom: '20px' }}>
              Провайдер Email
            </h2>

            {/* Enable/Disable Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', padding: '16px', backgroundColor: COLORS.background, borderRadius: '8px' }}>
              <div>
                <div style={{ color: COLORS.text, fontWeight: '600' }}>Включить отправку Email</div>
                <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Активирует отправку писем клиентам</div>
              </div>
              <button
                onClick={() => setSettings(prev => ({ ...prev, is_enabled: !prev.is_enabled }))}
                style={{
                  width: '50px',
                  height: '26px',
                  borderRadius: '13px',
                  backgroundColor: settings.is_enabled ? COLORS.success : COLORS.border,
                  border: 'none',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background-color 0.2s'
                }}
              >
                <div style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  position: 'absolute',
                  top: '2px',
                  left: settings.is_enabled ? '26px' : '2px',
                  transition: 'left 0.2s'
                }} />
              </button>
            </div>

            {/* Provider Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '8px' }}>
                Выберите провайдер
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                {['smtp', 'resend', 'sendgrid'].map(provider => (
                  <button
                    key={provider}
                    onClick={() => setSettings(prev => ({ ...prev, provider }))}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '16px',
                      backgroundColor: settings.provider === provider ? `${COLORS.primary}20` : COLORS.background,
                      border: `2px solid ${settings.provider === provider ? COLORS.primary : COLORS.border}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {getProviderIcon(provider)}
                    <span style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600', textTransform: 'uppercase' }}>
                      {provider}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* SMTP Settings */}
            {settings.provider === 'smtp' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                      SMTP Хост
                    </label>
                    <input
                      type="text"
                      value={settings.smtp.host}
                      onChange={(e) => handleUpdateSmtp('host', e.target.value)}
                      placeholder="smtp.gmail.com"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                      Порт
                    </label>
                    <input
                      type="number"
                      value={settings.smtp.port}
                      onChange={(e) => handleUpdateSmtp('port', parseInt(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                    Email (Username)
                  </label>
                  <input
                    type="email"
                    value={settings.smtp.username}
                    onChange={(e) => handleUpdateSmtp('username', e.target.value)}
                    placeholder="your-email@gmail.com"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      backgroundColor: COLORS.background,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      color: COLORS.text,
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                    Пароль / App Password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={settings.smtp.password}
                      onChange={(e) => handleUpdateSmtp('password', e.target.value)}
                      placeholder="••••••••••••••••"
                      style={{
                        width: '100%',
                        padding: '10px 40px 10px 12px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px'
                      }}
                    />
                    <button
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: COLORS.textSecondary
                      }}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                      Email отправителя
                    </label>
                    <input
                      type="email"
                      value={settings.smtp.from_email}
                      onChange={(e) => handleUpdateSmtp('from_email', e.target.value)}
                      placeholder="noreply@exdex.com"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                      Имя отправителя
                    </label>
                    <input
                      type="text"
                      value={settings.smtp.from_name}
                      onChange={(e) => handleUpdateSmtp('from_name', e.target.value)}
                      placeholder="ExDex"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.smtp.use_tls}
                      onChange={(e) => handleUpdateSmtp('use_tls', e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: COLORS.primary }}
                    />
                    <span style={{ color: COLORS.text, fontSize: '14px' }}>Использовать TLS</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.smtp.use_ssl}
                      onChange={(e) => handleUpdateSmtp('use_ssl', e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: COLORS.primary }}
                    />
                    <span style={{ color: COLORS.text, fontSize: '14px' }}>Использовать SSL</span>
                  </label>
                </div>
              </div>
            )}

            {/* Resend Settings */}
            {settings.provider === 'resend' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ padding: '12px', backgroundColor: `${COLORS.primary}10`, borderRadius: '8px', fontSize: '13px', color: COLORS.textSecondary }}>
                  💡 Получите API ключ на <a href="https://resend.com" target="_blank" rel="noreferrer" style={{ color: COLORS.primary }}>resend.com</a>
                </div>
                <div>
                  <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                    API Key
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={settings.resend.api_key}
                      onChange={(e) => handleUpdateApi('resend', 'api_key', e.target.value)}
                      placeholder="re_xxxxxxxxxx"
                      style={{
                        width: '100%',
                        padding: '10px 40px 10px 12px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px'
                      }}
                    />
                    <button
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: COLORS.textSecondary
                      }}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                      Email отправителя
                    </label>
                    <input
                      type="email"
                      value={settings.resend.from_email}
                      onChange={(e) => handleUpdateApi('resend', 'from_email', e.target.value)}
                      placeholder="noreply@yourdomain.com"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                      Имя отправителя
                    </label>
                    <input
                      type="text"
                      value={settings.resend.from_name}
                      onChange={(e) => handleUpdateApi('resend', 'from_name', e.target.value)}
                      placeholder="ExDex"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* SendGrid Settings */}
            {settings.provider === 'sendgrid' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ padding: '12px', backgroundColor: `${COLORS.primary}10`, borderRadius: '8px', fontSize: '13px', color: COLORS.textSecondary }}>
                  💡 Получите API ключ на <a href="https://sendgrid.com" target="_blank" rel="noreferrer" style={{ color: COLORS.primary }}>sendgrid.com</a>
                </div>
                <div>
                  <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                    API Key
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={settings.sendgrid.api_key}
                      onChange={(e) => handleUpdateApi('sendgrid', 'api_key', e.target.value)}
                      placeholder="SG.xxxxxxxxxx"
                      style={{
                        width: '100%',
                        padding: '10px 40px 10px 12px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px'
                      }}
                    />
                    <button
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: COLORS.textSecondary
                      }}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                      Email отправителя
                    </label>
                    <input
                      type="email"
                      value={settings.sendgrid.from_email}
                      onChange={(e) => handleUpdateApi('sendgrid', 'from_email', e.target.value)}
                      placeholder="noreply@yourdomain.com"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                      Имя отправителя
                    </label>
                    <input
                      type="text"
                      value={settings.sendgrid.from_name}
                      onChange={(e) => handleUpdateApi('sendgrid', 'from_name', e.target.value)}
                      placeholder="ExDex"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              style={{
                width: '100%',
                marginTop: '24px',
                padding: '14px',
                backgroundColor: COLORS.primary,
                color: '#000',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {saving ? <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={18} />}
              {saving ? 'Сохранение...' : 'Сохранить настройки'}
            </button>
          </div>

          {/* Right Column - Test Email */}
          <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '24px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: COLORS.text, marginBottom: '20px' }}>
              Тестирование
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                Email для тестового письма
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '6px',
                  color: COLORS.text,
                  fontSize: '14px'
                }}
              />
            </div>

            <button
              onClick={handleTestEmail}
              disabled={testing || !settings.is_enabled}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: settings.is_enabled ? COLORS.success : COLORS.border,
                color: settings.is_enabled ? '#fff' : COLORS.textSecondary,
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: testing || !settings.is_enabled ? 'not-allowed' : 'pointer',
                opacity: testing ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {testing ? <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={18} />}
              {testing ? 'Отправка...' : 'Отправить тестовое письмо'}
            </button>

            {!settings.is_enabled && (
              <p style={{ color: COLORS.textSecondary, fontSize: '12px', marginTop: '10px', textAlign: 'center' }}>
                Включите отправку email для тестирования
              </p>
            )}

            {/* Connection Status */}
            <div style={{ marginTop: '30px', padding: '16px', backgroundColor: COLORS.background, borderRadius: '8px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: COLORS.text, marginBottom: '12px' }}>
                Статус подключения
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: settings.is_enabled ? COLORS.success : COLORS.danger
                }} />
                <span style={{ color: settings.is_enabled ? COLORS.success : COLORS.danger, fontSize: '14px' }}>
                  {settings.is_enabled ? 'Активно' : 'Отключено'}
                </span>
              </div>
              <div style={{ marginTop: '10px', fontSize: '13px', color: COLORS.textSecondary }}>
                Провайдер: <strong style={{ color: COLORS.text }}>{settings.provider.toUpperCase()}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'templates' && (
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '24px' }}>
          {/* Templates List */}
          <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: COLORS.text, marginBottom: '16px' }}>
              Шаблоны
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {templates.map(template => (
                <button
                  key={template.id}
                  onClick={() => handlePreviewTemplate(template)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    backgroundColor: selectedTemplate?.id === template.id ? `${COLORS.primary}20` : COLORS.background,
                    border: `1px solid ${selectedTemplate?.id === template.id ? COLORS.primary : COLORS.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <div>
                    <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '500' }}>
                      {getTemplateTypeLabel(template.type)}
                    </div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>{template.name}</div>
                  </div>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: template.is_active ? COLORS.success : COLORS.danger
                  }} />
                </button>
              ))}
            </div>
          </div>

          {/* Template Preview / Editor */}
          <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '24px' }}>
            {editingTemplate ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: COLORS.text }}>
                    Редактирование: {editingTemplate.name}
                  </h3>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => setEditingTemplate(null)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        cursor: 'pointer'
                      }}
                    >
                      Отмена
                    </button>
                    <button
                      onClick={handleSaveTemplate}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: COLORS.primary,
                        border: 'none',
                        borderRadius: '6px',
                        color: '#000',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Save size={16} /> Сохранить
                    </button>
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                    Тема письма
                  </label>
                  <input
                    type="text"
                    value={editingTemplate.subject}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      backgroundColor: COLORS.background,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      color: COLORS.text,
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>
                    HTML контент
                  </label>
                  <textarea
                    value={editingTemplate.body_html}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, body_html: e.target.value }))}
                    rows={15}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: COLORS.background,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      color: COLORS.text,
                      fontSize: '13px',
                      fontFamily: 'monospace',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ padding: '12px', backgroundColor: COLORS.background, borderRadius: '8px', fontSize: '12px', color: COLORS.textSecondary }}>
                  <strong>Доступные переменные:</strong> {editingTemplate.variables?.map(v => `{{${v}}}`).join(', ')}
                </div>
              </>
            ) : selectedTemplate ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: COLORS.text }}>
                    {selectedTemplate.name}
                  </h3>
                  <button
                    onClick={() => setEditingTemplate(selectedTemplate)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: COLORS.primary,
                      border: 'none',
                      borderRadius: '6px',
                      color: '#000',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Edit3 size={16} /> Редактировать
                  </button>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Тема:</label>
                  <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '500' }}>{selectedTemplate.subject}</div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '8px', display: 'block' }}>
                    Предпросмотр:
                  </label>
                  <div
                    style={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      padding: '20px',
                      maxHeight: '400px',
                      overflow: 'auto'
                    }}
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>

                <div style={{ padding: '12px', backgroundColor: COLORS.background, borderRadius: '8px', fontSize: '12px', color: COLORS.textSecondary }}>
                  <strong>Переменные:</strong> {selectedTemplate.variables?.map(v => `{{${v}}}`).join(', ')}
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: COLORS.textSecondary }}>
                <FileText size={48} color={COLORS.border} style={{ marginBottom: '16px' }} />
                <p>Выберите шаблон для просмотра</p>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AdminEmailSettings;
